import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Loader2, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  
  // Prevent React 18 StrictMode from firing verification request twice
  const hasCalled = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg(t('auth.verify.noToken', 'No verification token provided.'));
      return;
    }

    if (hasCalled.current) return;
    hasCalled.current = true;

    (async () => {
      try {
        await axios.get(`${API}/auth/verify-email`, { params: { token } });
        setStatus('success');
      } catch (error) {
        setStatus('error');
        setErrorMsg(error.response?.data?.detail || t('auth.verify.error', 'Verification failed. The link may have expired or is invalid.'));
      }
    })();
  }, [token, t]);

  if (status === 'loading') {
    return (
      <FullScreenCard>
        <Loader2 className="w-10 h-10 animate-spin text-accent mb-4 mx-auto" />
        <h1 className="font-serif text-2xl font-light mb-2">{t('auth.verify.verifyingTitle', 'Verifying Email')}</h1>
        <p className="text-sm text-muted-foreground">{t('auth.verify.verifyingBody', 'Please wait while we confirm your email address...')}</p>
      </FullScreenCard>
    );
  }

  if (status === 'success') {
    return (
      <FullScreenCard>
        <div className="inline-flex w-14 h-14 rounded-full bg-accent/15 items-center justify-center mb-6">
          <Check className="w-7 h-7 text-accent" />
        </div>
        <h1 className="font-serif text-2xl font-light mb-2">{t('auth.verify.successTitle', 'Account Verified!')}</h1>
        <p className="text-sm text-muted-foreground mb-6 max-w-sm">
          {t('auth.verify.successBody', 'Your email has been successfully verified. You can now log in to your account.')}
        </p>
        <Button onClick={() => navigate('/')} className="rounded-full px-8">
          {t('auth.verify.goToLogin', 'Go to Login')}
        </Button>
      </FullScreenCard>
    );
  }

  return (
    <FullScreenCard>
      <div className="inline-flex w-14 h-14 rounded-full bg-destructive/10 items-center justify-center mb-6">
        <X className="w-7 h-7 text-destructive" />
      </div>
      <h1 className="font-serif text-2xl font-light mb-2">{t('auth.verify.errorTitle', 'Verification Failed')}</h1>
      <p className="text-sm text-muted-foreground mb-6 max-w-sm">
        {errorMsg}
      </p>
      <Button onClick={() => navigate('/')} variant="outline" className="rounded-full px-8">
        {t('auth.verify.backToHome', 'Back to Home')}
      </Button>
    </FullScreenCard>
  );
}

function FullScreenCard({ children }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12" data-testid="verify-email-page">
      <div className="max-w-md w-full text-center bg-card/40 border border-border rounded-2xl p-8 sm:p-10 fade-in">
        {children}
      </div>
    </div>
  );
}
